import { notEmpty } from '@talos/kyoko';
import type { GroupPanelViewState, SerializedDockview } from 'dockview';
import { uniq } from 'lodash-es';
import { LEAF, MAIN_GROUP_ID } from '../../../components/Layout/tokens';
import { getAddedPanels } from './getAddedPanels';
import { getLargestLayoutGroup } from './getLargestGroup';
import { getLayoutGroupById } from './getLayoutGroupById';

export function resetLayoutToDefault(
  currentLayout: SerializedDockview,
  defaultLayout: SerializedDockview
): SerializedDockview {
  const newLayout = structuredClone(defaultLayout);

  // Get all panels that's not in the initial layout
  const addedPanels = getAddedPanels(defaultLayout, currentLayout);

  // Find the main group and add the panels
  const mainGroup = getLayoutGroupById(newLayout, MAIN_GROUP_ID) ?? getLargestLayoutGroup(newLayout);
  if (mainGroup && mainGroup.type === LEAF) {
    const mainGroupData = mainGroup.data as GroupPanelViewState;
    const defaultPanels = mainGroupData.views.map(view => defaultLayout.panels[view]).filter(notEmpty);

    // Get panels from current layout's main group only
    const currentMainGroup = getLayoutGroupById(currentLayout, MAIN_GROUP_ID);
    const currentMainGroupViews =
      currentMainGroup?.type === LEAF ? (currentMainGroup.data as GroupPanelViewState).views : [];
    const currentMainGroupPanels = new Map(
      currentMainGroupViews.map(view => [currentLayout.panels[view]?.contentComponent, currentLayout.panels[view]])
    );

    // Remove default panels with contentTypes that are already in the current layout's main group
    const defaultPanelsToRestore = defaultPanels.filter(defaultPanel => {
      const currentPanel = currentMainGroupPanels.get(defaultPanel.contentComponent);
      return currentPanel == null || currentPanel.id === defaultPanel.id;
    });
    const defaultPanelsToRemove = defaultPanels.filter(defaultPanel => !defaultPanelsToRestore.includes(defaultPanel));
    const defaultViews = defaultPanelsToRestore.map(panel => panel.id);
    mainGroupData.views = uniq([...defaultViews, ...addedPanels]);

    newLayout.panels = { ...defaultLayout.panels, ...currentLayout.panels };
    defaultPanelsToRemove.forEach(panel => delete newLayout.panels[panel.id]);
    return newLayout;
  }
  return defaultLayout;
}
